import { useMemo, useContext, useCallback } from 'react';
import { useRouteMatch } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import axios from 'helpers/axios';
import { ToastContext } from 'contexts';
import { sortKeysToQueryString } from 'helpers';
import { locationsToRegionData } from 'helpers/locations';
import { useBaseInfiniteQuery } from 'helpers/hooks';
import {
  SORT_BY,
  PROJECT_STATUS,
  PROJECT_SOURCE,
  PROJECT_VIEW_STATUS,
} from 'constants/opportunities';
import {
  PROJECTS_SEARCH,
  PROJECTS_TYPES,
  ACCOUNTPROJECTS,
  SEARCH_PROFILES,
  SEARCH_PROFILES_ID,
  PROJECTS_SEARCH_PROFILE_ID,
} from 'constants/apis';

export const getSortQueryParams = (sortBy, statuses) => {
  const sortKeys = [];

  if (sortBy === SORT_BY.closingSoon) {
    // Special edge case where we want to show open projects before closed ones
    // while also sorting by `dateClose` in ascending order.
    if (statuses === PROJECT_STATUS.allOpportunities) {
      return { sortOpenBeforeClosed: true };
    }

    if (statuses === PROJECT_STATUS.open) {
      sortKeys.push({ id: SORT_BY.closingSoon, direction: 'asc' });
    } else {
      sortKeys.push({ id: SORT_BY.closingSoon, direction: 'desc' });
    }
  } else if (sortBy === SORT_BY.mostRecent) {
    sortKeys.push({ id: SORT_BY.mostRecent, direction: 'desc' });
  }

  return { sort: sortKeysToQueryString(sortKeys) };
};

export const splitOrganizations = organizations =>
  organizations.reduce(
    (result, { OrganizationName, OrganizationUUID }) => {
      if (OrganizationUUID) {
        result.organizationUuids.push(OrganizationUUID);
      } else {
        result.organizationNames.push(OrganizationName);
      }
      return result;
    },
    { organizationNames: [], organizationUuids: [] }
  );

/**
 * Checks which tab is currently active on the "Opportunities" page.
 * @returns {{tab: 'recommended' | 'following' | 'all' | 'saved' | null, id: number}} The active tab, and active searchProfileId
 */
export const useOpportunitiesActiveTab = () => {
  const match = useRouteMatch({
    path: '/opportunities/:tab(recommended|following|all|saved)?/:id(\\d+)?',
  });
  if (match) {
    const { tab, id } = match.params;
    return { tab, id: id ? parseInt(id) : null };
  }
  return { tab: null, id: null };
};

export const OPPORTUNITIES_SEARCH_QUERY_KEYS = {
  base: ['projects', 'search'],
  type: type => [...OPPORTUNITIES_SEARCH_QUERY_KEYS.base, type],
  dashboard: type => [...OPPORTUNITIES_SEARCH_QUERY_KEYS.type(type), 'dashboard'],
  search: (type, filters, params, { isDashboard = false } = {}) => [
    ...(isDashboard
      ? OPPORTUNITIES_SEARCH_QUERY_KEYS.dashboard(type)
      : OPPORTUNITIES_SEARCH_QUERY_KEYS.type(type)),
    filters,
    params,
  ],
  profile: id => [...OPPORTUNITIES_SEARCH_QUERY_KEYS.type('profile'), id],
  profileSearch: (id, filters, params) => [
    ...OPPORTUNITIES_SEARCH_QUERY_KEYS.profile(id),
    filters,
    params,
  ],
};

/**
 * Fetches a list of opportunity types.
 * @returns {import('react-query').useQuery} A `useQuery` hook to execute the fetch.
 */
export const useOpportunityTypes = (options = {}) => {
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);

  return useQuery(['projects', 'types'], () => axios.get(PROJECTS_TYPES).then(({ data }) => data), {
    onError: () => triggerToast({ message: t('generic_error') }),
    staleTime: 60 * 60 * 1000, // 1 hour.
    ...options,
  });
};

/**
 * Toggles the `IsFollowing` or `IsViewed` field for an opportunity on the
 * "Opportunities" page.
 * @returns {object} An object containing a `useMutation` hook and helpers to manage the cache.
 */
export const useToggleOpportunityStatus = () => {
  const { tab: activeTab } = useOpportunitiesActiveTab();
  const queryClient = useQueryClient();

  const removeOpportunityFromCache = useCallback((queryClient, variables) => {
    queryClient.setQueriesData(OPPORTUNITIES_SEARCH_QUERY_KEYS.type('following'), prev => {
      const newPages =
        prev?.pages.map(page => {
          const filteredOpportunities = page.data.filter(
            opportunity => opportunity.ProjectUUID !== variables.ProjectUUID
          );

          return {
            ...page,
            total: page.total - 1,
            data: filteredOpportunities,
          };
        }) ?? [];

      return { ...prev, pages: newPages };
    });
  }, []);

  const updateOpportunitiesCache = useCallback((queryClient, variables) => {
    queryClient.setQueriesData(OPPORTUNITIES_SEARCH_QUERY_KEYS.base, prev => {
      const newPages =
        prev?.pages.map(page => ({
          ...page,
          data: page.data.map(opportunity =>
            opportunity.ProjectUUID === variables.ProjectUUID
              ? {
                  ...opportunity,
                  IsFollowing: variables.IsFollowing,
                  IsViewed: variables.IsViewed,
                }
              : opportunity
          ),
        })) ?? [];

      return { ...prev, pages: newPages };
    });
  }, []);

  const mutation = useMutation(
    ({ ProjectUUID, IsFollowing, IsViewed }) =>
      axios.put(ACCOUNTPROJECTS(ProjectUUID), { isPinned: IsFollowing, isViewed: IsViewed }),
    {
      onMutate: async variables => {
        await queryClient.cancelQueries(OPPORTUNITIES_SEARCH_QUERY_KEYS.base, { exact: false });

        const previousOpportunitiesSearchSnapshots = queryClient.getQueriesData({
          queryKey: OPPORTUNITIES_SEARCH_QUERY_KEYS.base,
          exact: false,
        });

        if (
          activeTab === 'following' &&
          variables.trigger === 'toggle-follow' &&
          !variables.IsFollowing
        ) {
          removeOpportunityFromCache(queryClient, variables);
        }

        updateOpportunitiesCache(queryClient, variables);

        return { previousOpportunitiesSearchSnapshots };
      },
      onError: (error, variables, context) => {
        context.previousOpportunitiesSearchSnapshots?.forEach(snapshot => {
          const [queryKey, data] = snapshot;
          queryClient.setQueryData(queryKey, data);
        });
      },
      onSuccess: (data, variables) => {
        if (variables.trigger === 'toggle-follow') {
          queryClient.invalidateQueries(OPPORTUNITIES_SEARCH_QUERY_KEYS.type('following'), {
            exact: false,
          });
        }
      },
    }
  );

  return { mutation, updateOpportunitiesCache };
};

const SAVED_SEARCH_PROFILES_KEY_BASE = ['searchProfiles'];
export const SAVED_SEARCH_PROFILES_KEYS = {
  base: SAVED_SEARCH_PROFILES_KEY_BASE,
  summary: [...SAVED_SEARCH_PROFILES_KEY_BASE, 'summary'],
  id: id => [...SAVED_SEARCH_PROFILES_KEY_BASE, id],
};

export const useInfiniteSavedSearchProfiles = (options = {}) => {
  const { dataFlattened, total, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useBaseInfiniteQuery({
      queryKey: SAVED_SEARCH_PROFILES_KEYS.summary,
      fetch: ({ page }) => axios.get(SEARCH_PROFILES, { params: { limit: 25, page } }),
      options: { staleTime: 10 * 60 * 1000, ...options }, // 10 mins.
    });

  return {
    searchProfiles: dataFlattened,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
};

export const useSpecificSavedSearchProfile = (id, options = {}) => {
  return useQuery(
    SAVED_SEARCH_PROFILES_KEYS.id(id),
    () => axios.get(SEARCH_PROFILES_ID(id)).then(({ data }) => data),
    { staleTime: 5 * 60 * 1000, enabled: !!id, ...options } // 5 mins.
  );
};

export const useDeleteSearchProfile = () => {
  const queryClient = useQueryClient();

  const removeProfileFromCache = useCallback((queryClient, id) => {
    queryClient.setQueryData(SAVED_SEARCH_PROFILES_KEYS.summary, prev => {
      const newPages =
        prev?.pages.map(page => {
          const filteredProfiles = page.data.filter(profile => profile.id !== id);

          return {
            ...page,
            total: page.total - 1,
            data: filteredProfiles,
          };
        }) ?? [];

      return { ...prev, pages: newPages };
    });
  }, []);

  const mutation = useMutation(id => axios.delete(SEARCH_PROFILES_ID(id)), {
    onMutate: async id => {
      await Promise.all([
        queryClient.cancelQueries(SAVED_SEARCH_PROFILES_KEYS.summary),
        queryClient.cancelQueries(SAVED_SEARCH_PROFILES_KEYS.id(id)),
        queryClient.cancelQueries(OPPORTUNITIES_SEARCH_QUERY_KEYS.profile(id)),
      ]);
      const previousProfileSnapshots = queryClient.getQueryData(SAVED_SEARCH_PROFILES_KEYS.summary);
      removeProfileFromCache(queryClient, id);

      return { previousProfileSnapshots };
    },
    onError: (error, variables, context) => {
      context.previousProfileSnapshots?.forEach(snapshot => {
        const [queryKey, data] = snapshot;
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (result, id) => {
      queryClient.removeQueries(SAVED_SEARCH_PROFILES_KEYS.id(id));
    },
  });

  return { mutation };
};

const removeInactiveSearches = queryClient => {
  // Allows for better performance when doing optimistic updates on paginated queries
  // since we'll only ever have to map on the current query key instead of everything
  // in the cache.
  queryClient.removeQueries({
    queryKey: OPPORTUNITIES_SEARCH_QUERY_KEYS.base,
    type: 'inactive',
    exact: false,
    predicate: query => !query.queryKey.includes('dashboard'),
  });
};

const mapFiltersAndParams = ({
  type,
  statuses = PROJECT_STATUS.open,
  sources = PROJECT_SOURCE.allSources,
  viewStatus = PROJECT_VIEW_STATUS.allStatuses,
  sortBy = SORT_BY.mostRecent,
  locations = [],
  organizations = [],
  types = [],
  search = '',
  limit = 10,
}) => {
  const filters = {
    ...splitOrganizations(organizations),
    statuses: statuses.split(','),
    sources: sources.split(','),
    viewedProjects: viewStatus.split(','),
    projectTypes: types,
    search,
    regionData: locationsToRegionData(locations),
  };
  if (type) {
    filters.type = type === 'following' ? 'pinned' : type;
  }
  const params = { limit, ...getSortQueryParams(sortBy, statuses) };
  return { filters, params };
};

/**
 * Fetches a paginated list of opportunities according to the `type` and `filters`
 * provided.
 * @returns {object} An object containing pagination helpers and an array of
 * flattened opportunities.
 */
export const useInfiniteOpportunitiesSearch = (type, filterArgs, options = {}) => {
  const queryClient = useQueryClient();

  const { filters, params } = useMemo(() => {
    return mapFiltersAndParams({ type, ...filterArgs });
  }, [filterArgs, type]);

  const { isDashboard = false, ...opts } = options;

  const {
    dataFlattened,
    total,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useBaseInfiniteQuery({
    queryKey: OPPORTUNITIES_SEARCH_QUERY_KEYS.search(type, filters, params, { isDashboard }),
    fetch: ({ page }) => axios.post(PROJECTS_SEARCH, filters, { params: { ...params, page } }),
    options: {
      staleTime: 5 * 60 * 1000, // 5 mins.
      onSuccess: () => {
        removeInactiveSearches(queryClient);
      },
      ...opts,
    },
  });

  return {
    opportunities: dataFlattened,
    total,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
};

export const useInfiniteOpportunitiesSavedSearchProfile = (id, filterArgs, options = {}) => {
  const queryClient = useQueryClient();

  const { filters, params } = useMemo(() => {
    return mapFiltersAndParams({ ...filterArgs });
  }, [filterArgs]);

  const {
    dataFlattened,
    total,
    isFetching,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useBaseInfiniteQuery({
    queryKey: OPPORTUNITIES_SEARCH_QUERY_KEYS.profileSearch(id, filters, params),
    fetch: ({ page }) =>
      axios.post(PROJECTS_SEARCH_PROFILE_ID(id), filters, { params: { ...params, page } }),
    options: {
      staleTime: 5 * 60 * 1000,
      enabled: !!id,
      onSuccess: () => {
        removeInactiveSearches(queryClient);
      },
      ...options,
    }, // 5 mins.
  });

  return {
    opportunities: dataFlattened,
    total,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
};
